import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  AppBar,
  Box,
  Button,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Description as DescriptionIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  CloudUpload as CloudUploadIcon,
  ViewModule as ViewModuleIcon,
  AccountCircle,
  Logout,
  Lock as LockIcon,
  AdminPanelSettings,
  CheckCircle,
  Assignment as AssignmentIcon,
  PhoneForwarded as SalesIcon,
  AccountBalanceWallet as FundedIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import LanguageToggle from './LanguageToggle';
import { BRAND } from './login/brand';

const drawerWidth = 240;
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

// Sidebar palette mirrors the login page hero (deep brown + copper/sand accents).
const SIDEBAR = {
  bg: `linear-gradient(180deg, ${BRAND.brownDeep} 0%, ${BRAND.brown} 100%)`,
  header: BRAND.brownDeep,
  text: 'rgba(255,248,238,0.78)',
  textMuted: 'rgba(255,248,238,0.32)',
  hoverBg: 'rgba(232,189,118,0.10)',
  activeBg: 'rgba(232,189,118,0.16)',
  accent: BRAND.sand,
  divider: 'rgba(232,189,118,0.14)',
};

const navItemSx = (active) => ({
  borderRadius: '6px',
  mb: 0.25,
  color: active ? '#fff' : SIDEBAR.text,
  bgcolor: active ? SIDEBAR.activeBg : 'transparent',
  boxShadow: active ? `inset 3px 0 0 ${SIDEBAR.accent}` : 'none',
  '& .MuiListItemIcon-root': { color: active ? SIDEBAR.accent : 'inherit' },
  '&:hover': { bgcolor: active ? SIDEBAR.activeBg : SIDEBAR.hoverBg, color: '#fff' },
  '&.Mui-selected, &.Mui-selected:hover': { bgcolor: SIDEBAR.activeBg },
  '&.Mui-disabled': { color: SIDEBAR.textMuted, opacity: 1 },
});

const Layout = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { user, logout, isAdmin, isSales } = useAuth();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [loadingApp, setLoadingApp] = useState(null); // Track which app link is loading

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Build URL with SSO token for external apps
  const buildRedirectUrl = async (baseUrl, appType = 'application') => {
    const url = new URL(baseUrl);
    
    // Set appropriate return URL based on app type
    const coreloanhubUrl = import.meta.env.VITE_CORELOANHUB_URL || 'https://coreloanhub.com';
    const returnParam = appType === 'pfs' ? 'pfs_submitted=true' : 'submitted=true';
    const returnUrl = `${coreloanhubUrl}/dashboard?${returnParam}&source=${appType === 'pfs' ? 'pfs-copilot' : 'financing-app'}`;
    url.searchParams.append('returnUrl', returnUrl);
    
    if (user?.email) {
      url.searchParams.append('email', user.email);
    }
    
    // Generate single-use authentication token
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/auth/generate-single-use-token`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.success) {
        url.searchParams.append('authToken', response.data.token);
        console.log('Generated auth token for external app');
      }
    } catch (error) {
      console.error('Failed to generate auth token:', error);
      // Continue without token - user will need to login manually
    }
    
    return url.toString();
  };

  const handleAppLinkClick = async (e, item) => {
    e.preventDefault();
    setLoadingApp(item.url);
    
    try {
      const redirectUrl = await buildRedirectUrl(item.url, item.appType);
      window.open(redirectUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening app:', error);
      // Fallback to direct URL
      window.open(item.url, '_blank', 'noopener,noreferrer');
    } finally {
      setLoadingApp(null);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  const menuItems = [
    { text: t('nav.dashboard'), icon: <DashboardIcon />, path: '/dashboard' },
    { text: t('nav.myApplication') || 'My Application', icon: <AssignmentIcon />, path: '/my-application' },
    { text: t('nav.programs'), icon: <ViewModuleIcon />, path: '/programs' },
    { text: t('nav.documents'), icon: <CloudUploadIcon />, path: '/documents' },
    { text: 'Debt Schedule', icon: <ReceiptIcon />, path: '/debt-schedule' },
    { text: 'My PFS', icon: <AccountBalanceIcon />, path: '/pfs' },
  ];

  // Core Loan Hub app links (same company, different tools)
  const appLinks = [
    { 
      text: t('nav.coreFundingApplication') || 'Core Funding Application', 
      icon: <DescriptionIcon />, 
      url: import.meta.env.VITE_FINANCING_APP_URL || 'https://app.corebusinesscapital.com',
      appType: 'application',
    },
    { 
      text: t('nav.financialStatement'), 
      icon: <AccountBalanceIcon />, 
      url: import.meta.env.VITE_PFS_URL || 'https://pfs.corebusinesscapital.com',
      appType: 'pfs',
    },
  ];

  // Internal links (for future implementation)
  const internalLinks = [
    { text: t('layout.approvals'), icon: <CheckCircle />, path: '/approvals', disabled: true },
  ];

  if (isAdmin) {
    menuItems.push({ text: 'UW Workbook', icon: <ReceiptIcon />, path: '/uw-workbook' });
    menuItems.push({ text: t('nav.admin'), icon: <AdminPanelSettings />, path: '/admin' });
  }

  if (isSales) {
    menuItems.push({ text: 'Sales CRM', icon: <SalesIcon />, path: '/sales' });
    menuItems.push({ text: 'Funded Book', icon: <FundedIcon />, path: '/sales/funded' });
  }

  // Longest matching path wins so "/sales/funded" doesn't also highlight "/sales".
  const activePath = menuItems
    .map((i) => i.path)
    .filter((p) => pathname === p || pathname.startsWith(`${p}/`))
    .sort((a, b) => b.length - a.length)[0];

  const goTo = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const drawer = (
    <Box sx={{ minHeight: '100%', background: SIDEBAR.bg }}>
      <Toolbar sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        py: 2,
        bgcolor: SIDEBAR.header,
        borderBottom: `1px solid ${SIDEBAR.divider}`,
      }}>
        <img 
          src="/images/logo.png" 
          alt="Core Loan Hub" 
          style={{ height: '50px', width: 'auto' }}
        />
      </Toolbar>
      <Box sx={{ px: 1.5, pt: 2, pb: 1 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={() => goTo('/documents')}
          sx={{
            py: 1.1,
            borderRadius: '8px',
            color: '#fff',
            fontWeight: 700,
            lineHeight: 1.25,
            background: BRAND.gradient,
            boxShadow: '0 4px 14px rgba(172,104,46,0.35)',
            '&:hover': { background: BRAND.gradientHover, boxShadow: '0 6px 18px rgba(172,104,46,0.45)' },
          }}
        >
          {t('nav.uploadStatements', 'Upload Bank Statements')}
        </Button>
      </Box>
      <List sx={{ px: 1, pt: 0.5 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => goTo(item.path)}
              selected={activePath === item.path}
              sx={navItemSx(activePath === item.path)}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: activePath === item.path ? 600 : 500 }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ borderColor: SIDEBAR.divider }} />
      <List sx={{ px: 1, pt: 1 }}>
        {appLinks.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton 
              onClick={(e) => handleAppLinkClick(e, item)}
              disabled={loadingApp === item.url}
              sx={navItemSx(false)}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {loadingApp === item.url ? (
                  <CircularProgress size={20} sx={{ color: SIDEBAR.accent }} />
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText
                primary={loadingApp === item.url ? t('layout.connecting') : item.text}
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ borderColor: SIDEBAR.divider }} />
      <Typography
        variant="caption"
        sx={{
          px: 2, py: 1.5, display: 'block',
          color: SIDEBAR.textMuted,
          textTransform: 'uppercase',
          letterSpacing: '0.09em',
          fontSize: '0.65rem',
          fontWeight: 600,
        }}
      >
        {t('nav.comingSoon')}
      </Typography>
      <List sx={{ px: 1 }}>
        {internalLinks.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              disabled={item.disabled}
              sx={navItemSx(false)}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: '#FFFFFF',
          color: BRAND.ink,
          boxShadow: 'none',
          borderBottom: `1px solid ${BRAND.border}`,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img 
              src="/images/logo-ob.jpg" 
              alt="Core Loan Hub" 
              style={{ height: '35px', width: 'auto' }}
            />
          </Box>
          <LanguageToggle />
          <Box 
            sx={{ 
              display: { xs: 'none', md: 'flex' }, 
              flexDirection: 'column', 
              alignItems: 'flex-end', 
              mr: 1 
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
              {user?.email}
            </Typography>
          </Box>
          <IconButton onClick={handleMenuOpen} color="inherit">
            <Avatar sx={{ bgcolor: BRAND.copperDark, fontWeight: 700 }}>
              {user?.firstName?.charAt(0)}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem disabled>
              <Typography variant="body2">
                {user?.firstName} {user?.lastName}
              </Typography>
            </MenuItem>
            <MenuItem disabled>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { handleMenuClose(); navigate('/change-password'); }}>
              <ListItemIcon>
                <LockIcon fontSize="small" />
              </ListItemIcon>
              Change Password
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              {t('nav.logout')}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background: SIDEBAR.bg,
              color: 'rgba(255,255,255,0.85)',
              borderRight: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background: SIDEBAR.bg,
              color: 'rgba(255,255,255,0.85)',
              borderRight: 'none',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          overflowX: 'hidden',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
